/**
 * DEFUSE S10 — the DIMENSION arm: the emitted-point / direction / bounds
 * mappers, the arrow-glyph affine placement, and the rigid per-primitive
 * transform of a block-child dimension (the AutoCAD "*D anonymous-block bake,
 * then transform the INSERT" model).
 *
 * Split out of `./blockTransform.ts` (which re-exports the public surface and
 * carries the family's module documentation) purely for file size — the
 * emitter contract and every scalar convention are unchanged. Depends on
 * `./blockInsertTransform.ts` and `./blockArcEllipseMapping.ts`.
 */

import { applyMat2, blockLinearMatrix } from "@dxf-ai-editor/cad-core";
import type { Mat2 } from "@dxf-ai-editor/cad-core";

import type { ArrowVariant, DimensionPrimitive, Vec3 } from "../../types";
import { glyphSizeAxis } from "../../dimensions/arrowMarkerMesh";
import { applyOffset, unapplyOffset } from "../../coordinateOrigin";

import {
  blockLinearOfTransform,
  blockScalarRotationDeg,
  isMirroredTransform,
  normalizeRad,
  transformBlockChildPoint,
  transformBlockDirection,
  type BlockInsertTransform,
} from "./blockInsertTransform";
import {
  mapDirectionRad,
  transformEllipseAxes,
} from "./blockArcEllipseMapping";

/**
 * # Transforming ALREADY-EMITTED primitives (the "bake local, then place" arm)
 *
 * Two block children are NOT point-transformable leaves: DIMENSION and
 * MULTILEADER derive their geometry from style/entity SCALARS that a transform
 * of the definition points cannot carry, so both are baked in the block-LOCAL
 * frame by their own production adapter and the emitted primitives are then
 * transformed rigidly (`renderBlockChildDimension` in `blockFromEntity.ts` /
 * `./blockChildMultileader.ts`). The mappers below are the shared seam those
 * arms use, so an emitted primitive can never drift from what
 * `transformBlockChildPoint` / `transformBlockDirection` do for every other
 * child.
 *
 * The emitters bake `applyOffset` into every point they write, so the mapper
 * for EMITTED points is the origin-offset round trip `unapplyOffset` →
 * transform → `applyOffset` (the transform itself works in RAW DXF
 * coordinates — {@link BlockInsertTransform}'s `insertion` doc).
 */
export function emittedPointMapper(
  basePoint: Vec3,
  t: BlockInsertTransform,
): (p: Vec3) => Vec3 {
  return (p) =>
    applyOffset(transformBlockChildPoint(unapplyOffset(p), basePoint, t));
}

/** Emitted UNIT direction → the linear part, re-normalised (a direction takes
 *  no translation, and the consumers store unit vectors). */
export function emittedUnitDirectionMapper(
  t: BlockInsertTransform,
): (d: Vec3) => Vec3 {
  return (d) => {
    const v = transformBlockDirection(d, t);
    const len = Math.hypot(v.x, v.y, v.z);
    return len > 0 ? { x: v.x / len, y: v.y / len, z: v.z / len } : v;
  };
}

/**
 * AABB of the block-transformed image of a LOCAL emitted AABB — the bounds
 * twin of {@link emittedPointMapper}, shared by both bake-then-place arms.
 *
 * An affine maps the local box's convex hull onto a parallelogram, so the AABB
 * of the four transformed CORNERS contains the image of every point the local
 * box contained: loose under rotation, never short. That containment is what
 * keeps the emitted geometry inside the INSERT metadata bounds `cad-core`'s
 * `blockDefinitionWalk` derives from the SAME local extents.
 */
export function transformEmittedBounds(
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  basePoint: Vec3,
  t: BlockInsertTransform,
): { minX: number; minY: number; maxX: number; maxY: number } {
  const xfPoint = emittedPointMapper(basePoint, t);
  const corners = [
    { x: bounds.minX, y: bounds.minY, z: 0 },
    { x: bounds.maxX, y: bounds.minY, z: 0 },
    { x: bounds.minX, y: bounds.maxY, z: 0 },
    { x: bounds.maxX, y: bounds.maxY, z: 0 },
  ].map(xfPoint);
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}

/**
 * The arrow / tick GLYPH's world map: the enclosing INSERT's 2×2 linear part
 * composed onto the pose the block-child emitter baked in the block-LOCAL frame,
 *
 *     glyphLinear = L · Rz(atan2 direction) · S(size, size)
 *
 * — i.e. exactly what {@link transformDimensionPrimitive} (and its MLEADER twin
 * `transformMultileaderPrimitive` in `./blockChildMultileader.ts`) does to every
 * dim-line / leader / extension-line POSITION, applied to the marker prototype's
 * own axes instead. The two columns are the world images of the prototype's
 * local X (the body axis, running back from the tip —
 * `dimensions/arrowMarkerMesh.ts`) and local Y (the half-width axis), so the
 * mesh builder can place a stretched or SHEARED marker that the similarity pair
 * `(direction, size)` cannot describe.
 *
 * # ONE truth for both the DIMENSION and MLEADER arms
 *
 * A block-child DIMENSION marker (`arrow` / `tick` `DimensionPrimitive`) and a
 * block-child MLEADER arrowhead (`arrow` `MultileaderPrimitive`) are the SAME
 * unit prototype under the SAME `T(tip)·Rz(direction)·S(size, size)` local pose
 * (`arrowMarkerMesh.ts`), and both carry an `ArrowVariant` — the MLEADER
 * emitter's `multileaderGeometry.ts` builds it with `dimblkToVariant`, so an
 * MLEADER whose `arrowheadBlock` is `_OBLIQUE` / `_ARCHTICK` produces an `arrow`
 * primitive carrying a TICK variant. So both arms place the glyph through this
 * one helper; there is one representation of "a marker glyph's world placement",
 * not two, and neither arm can read the companion `size` off the wrong axis.
 *
 * # Why the scalar pair was wrong (Codex PR #784 round 11 P2 — DIMENSION twin)
 *
 * `direction` + `size` has 2 degrees of freedom; `L` has 4. Every round up to
 * this one reduced the enclosing affine to a transformed unit direction plus
 * `size · |scaleX|`, and `dimensions/arrowInstanceMesh.ts
 * ::buildArrowInstanceMeshes` then applied that ONE scalar to BOTH prototype
 * axes. A VERTICAL dim line inside an INSERT scaled `(2, 3)` must grow its
 * arrowheads three times as long (their bodies run along Y) and twice as wide,
 * but the arm emitted both at 2 — with DIMASZ 2.5 the body reached 5.0 world
 * units back from the tip where the INSERT stretches it to 7.5. A DIAGONAL
 * (aligned) dim line lost the shear entirely: under `S(2, 3)` the images of the
 * prototype's two axes stop being perpendicular, which no `Rz·S` pose can
 * represent at all. This is the SAME product `transformBlockChildPoint` applies
 * to the dim line the markers cap, so head and line can no longer disagree.
 *
 * `L` comes from `cad-core`'s `blockLinearMatrix(blockLinearOfTransform(t))` —
 * the one-truth `R(θ)·S(a,b)·R(φ)` matrix, inner rotation included — never a
 * second derivation of the same triple. The seam it ships on is the SAME
 * optional `glyphLinear` the MLEADER arm added (`./blockChildMultileader.ts`),
 * carried by the shared `ArrowInstanceInput`, so there is one representation of
 * "a marker glyph's world placement", not two.
 *
 * # The companion SCALAR stays populated and stays HONEST
 *
 * `size` is READ OFF the same map rather than derived a second time, so the
 * summary the map's non-consumers draw (the PDF exporter's
 * `print/pdf/converters/arrowGeometry.ts`, which poses a similarity) can never
 * disagree with the map itself. Which axis it is read along is the GLYPH's own
 * business, so it is asked of `arrowMarkerMesh.ts::glyphSizeAxis`: an arrowhead
 * measures along its body (local X ⇒ `size · |L·d̂|`), an `oblique` tick along
 * its slash (local Y ⇒ `size · |L·n̂|`), an `arch-tick` along its 45° slash.
 * `|Rz(θ)·S(s,s) · u| === s` for every unit `u`, so a UNIFORM (or mirrored-
 * uniform) INSERT reproduces the previous `size · |scaleX|` exactly, and the
 * value stays non-negative — a mirror still never negates a length.
 */
export function placeGlyphAffine(
  direction: Vec3,
  size: number,
  variant: ArrowVariant,
  t: BlockInsertTransform,
): { size: number; glyphLinear: Mat2 } {
  const l = blockLinearMatrix(blockLinearOfTransform(t));
  // The emitter's direction is already a UNIT vector (both `dimensionGeometry.ts`
  // and `multileaderGeometry.ts` build every one with `unitVec2`, whose
  // degenerate case is `{1, 0}`), so the local pose is a pure rotation × the
  // uniform marker size:
  // `Rz(rot)·S(size, size)` has columns `size·(cos, sin)` and
  // `size·(−sin, cos)` — `direction` scaled, and its left normal — so no
  // `atan2` round-trip is needed to rebuild them.
  const col0 = applyMat2(l, direction.x * size, direction.y * size);
  const col1 = applyMat2(l, -direction.y * size, direction.x * size);
  const axis = glyphSizeAxis(variant);
  return {
    size: Math.hypot(
      col0.x * axis.x + col1.x * axis.y,
      col0.y * axis.x + col1.y * axis.y,
    ),
    glyphLinear: { m00: col0.x, m01: col1.x, m10: col0.y, m11: col1.y },
  };
}

/**
 * The world image of a dimension ARC / ELLIPSE ARC under the block INSERT's
 * linear part — Codex PR #784 round 12 P2, "Preserve the affine image of
 * dimension arcs".
 *
 * # Why one radius was wrong
 *
 * `direction`+`size` had 2 DOF where the glyph needed 4
 * ({@link placeGlyphAffine}); `radius` has ONE where an arc needs the same 4.
 * A DIMANGULAR arc is emitted CIRCULAR in the block-LOCAL frame
 * (`adapters/geom/dimensionGeometry.ts::emitAngular`), and the image of a
 * circle under a general 2×2 is an ELLIPSE — circular only when the map is a
 * similarity. The pre-fix arm kept `kind: "arc"` and multiplied the radius by
 * `|scaleX|` alone, so `batchedLayer.ts`'s `sampleArc` drew a circle at the
 * X-axis radius: under `S(2, 3)` a local radius-7.8101 arc's vertical apex
 * belongs at `3 × 7.8101 = 23.4306` and was drawn at `2 × 7.8101 = 15.6202`
 * — while the two arrowheads that CAP that arc, and the extension lines that
 * meet it, ride the full affine through `emittedPointMapper` /
 * {@link placeGlyphAffine}. The arc therefore ended ~7.81 world units short of
 * its own arrowhead tips, with ~4.29 units of bulge error at the 45° apex.
 *
 * # The image, exactly
 *
 * `L · (r·cos θ, r·sin θ)` sweeps `u·cos θ + v·sin θ` for the CONJUGATE
 * semi-diameters `u = L·major`, `v = L·minor` — an ellipse, generally rotated
 * and generally with a different ratio. {@link transformEllipseAxes} is the
 * repo's ONE truth for recovering that image's PRINCIPAL axes (the same helper
 * the hatch `ellipticArc` edge arm and `transformEllipseGeometry` use), and it
 * also returns:
 *
 *   - `tMajor` — the SOURCE parameter the image's major axis sits at, so the
 *     parameters must be RE-ANCHORED by `θ − tMajor` (a non-uniform map does
 *     NOT preserve parameter angles: the endpoints only land on the images of
 *     the source endpoints once they are re-anchored);
 *   - `flip` — whether the image parameterisation runs CW (a MIRRORED insert,
 *     `det L < 0`). `tessellateEllipse` always sweeps CCW from `startParam`,
 *     so a flipped image negates each parameter AND swaps the two, exactly as
 *     the `arc` arm swaps its mapped endpoints and the hatch arm toggles
 *     `counterclockwise`.
 *
 * A CIRCLE is the `ratio === 1`, `majorAxis = (r, 0)` member of this family —
 * so the promotion and the re-transform of an already-promoted primitive are
 * the SAME call, not two mechanisms. Only a NON-similarity map routes here
 * (`transformDimensionPrimitive` keeps the circular arm, byte-identical, for
 * `|scaleX| === |scaleY|`), so no existing fixture moves.
 *
 * `center` rides the same offset-round-trip point mapper every other emitted
 * point does; `majorAxis.z` stays 0 because the dimension emitter is planar in
 * the block XY plane and the block map's z half is a pure scale that cannot
 * tilt it. A DEGENERATE source (zero radius, or a scale so small the image
 * axes vanish) yields a zero-length major axis, which the shared sampler draws
 * as nothing — the same invisible result the coincident-point run of
 * `sampleArc` produced for it.
 */
export function transformDimensionEllipseArc(
  source: {
    /** LOCAL (emitter-frame, offset-applied) ellipse centre. */
    center: Vec3;
    /** LOCAL major semi-axis VECTOR relative to `center`. */
    majorAxis: { x: number; y: number };
    /** LOCAL minor/major ratio (1 for a circular arc). */
    ratio: number;
    /** LOCAL start/end parameter (radians, CCW from the major axis). */
    startParam: number;
    endParam: number;
    colorHex: string;
  },
  basePoint: Vec3,
  t: BlockInsertTransform,
): Extract<DimensionPrimitive, { kind: "elliptic-arc" }> {
  const axes = transformEllipseAxes(source.majorAxis, source.ratio, t);
  const anchor = (rad: number): number =>
    normalizeRad(axes.flip ? -(rad - axes.tMajor) : rad - axes.tMajor);
  const start = anchor(source.startParam);
  const end = anchor(source.endParam);
  return {
    kind: "elliptic-arc",
    center: emittedPointMapper(basePoint, t)(source.center),
    majorAxis: { x: axes.major.x, y: axes.major.y, z: 0 },
    ratio: axes.ratio,
    // A flipped image runs CW through the same points, so the CCW sweep that
    // draws the SAME visual arc starts at the image of the source END.
    startParam: axes.flip ? end : start,
    endParam: axes.flip ? start : end,
    colorHex: source.colorHex,
  };
}

/**
 * Rigidly transform ONE `DimensionPrimitive` by the block INSERT affine — the
 * AutoCAD "*D anonymous-block bake, then transform the INSERT" model. The
 * dimension geometry is generated in LOCAL (block) space by `dimensionFromEntity`
 * (`renderBlockChildDimension` in `blockFromEntity.ts` owns that call), where the
 * extension lines / dim line / arrows / gaps / text sit at the DIMSTYLE-driven
 * DIMASZ / DIMEXO / DIMEXE / DIMGAP / DIMTXT the emitter dictates; then EVERY
 * emitted primitive rides the SAME affine the point/direction helpers apply, so:
 *
 *   - block SCALE carries into the DIMASZ arrow/tick glyph (the FULL 2×2 map,
 *     not one scalar — see {@link placeGlyphAffine}), the DIMEXO/DIMEXE
 *     extension lengths + DIMGAP text gap (baked into the transformed endpoint
 *     positions) and the DIMTXT-or-entity text height uniformly (@codex #510
 *     "Scale dimension style sizes in block transforms");
 *   - block ROTATION rotates the whole dimension — the horizontal-only linear
 *     emitter no longer collapses a 90°-rotated linear dim to `x1 === x2`
 *     (@codex #510 "Preserve block rotation for linear dimension children");
 *   - the LOCAL `0`-sentinel defPoint fallback + absent-extension-point default
 *     are resolved in local space by `dimensionFromEntity` BEFORE this affine,
 *     so a translation cannot destroy them (@codex #510 findings 1 + 4).
 *
 * Points ride the FULL affine THROUGH the origin-offset round-trip — the emitter
 * bakes `applyOffset` into every point, so we `unapplyOffset` → transform → re-
 * `applyOffset`. Directions ride the linear part (re-normalised). The arrow/tick
 * GLYPH rides the FULL 2×2 linear part, shipped as the primitive's optional
 * `glyphLinear` ({@link placeGlyphAffine}) with its `size` scalar read back off
 * that map — Codex PR #784 round 11 P2; the previous `size · |scaleX|` lost the
 * per-axis stretch and the shear. Text HEIGHT rides |scaleY| (the Text scalar
 * convention); text ROTATION folds the scalar-rotation convention.
 *
 * ARCS ride the same rule as the glyphs (Codex PR #784 round 12 P2): under a
 * SIMILARITY map (`|scaleX| === |scaleY|`, mirror included) the image is still
 * a circle, so the sweep endpoints ride the rotation/reflection table and a
 * mirror swaps the CCW endpoints (the dim-arc twin of the hatch arc-edge
 * `counterclockwise` flip) — byte-identical to every earlier round. Under a
 * NON-UNIFORM map the image is an ELLIPSE arc, and the primitive is PROMOTED
 * to `elliptic-arc` rather than reduced to `radius · |scaleX|`
 * ({@link transformDimensionEllipseArc}).
 */
export function transformDimensionPrimitive(
  prim: DimensionPrimitive,
  basePoint: Vec3,
  t: BlockInsertTransform,
): DimensionPrimitive {
  const xfPoint = emittedPointMapper(basePoint, t);
  const xfDir = emittedUnitDirectionMapper(t);
  // |scaleX| for the arc radius (Circle convention — and, since round 12, only
  // ever on the SIMILARITY branch where `|scaleX| === |scaleY|` makes it the
  // exact isotropic factor), |scaleY| for text height (Text convention); the
  // scalar-rotation sum folds the double-negative 180° (see
  // `transformBlockChild`). The arrow/tick markers no longer read
  // `radiusScale` at all — their glyph rides the full 2×2 (`placeGlyphAffine`).
  const radiusScale = Math.abs(t.scale.x);
  const heightScale = Math.abs(t.scale.y);
  const scalarRotationDeg = blockScalarRotationDeg(t);
  switch (prim.kind) {
    case "extension-line":
    case "dim-line":
    case "leader":
      return { ...prim, a: xfPoint(prim.a), b: xfPoint(prim.b) };
    case "arc": {
      // NON-UNIFORM enclosing map ⇒ the image of this circle is an ELLIPSE:
      // promote instead of reducing the 2×2 to one radius
      // ({@link transformDimensionEllipseArc}). The first predicate is the SAME
      // `|scaleX| === |scaleY|` similarity test `mapDirectionRad` branches on,
      // so the two arms never disagree about which case they are in.
      //
      // The second is the ONE case where the arc and ellipse SAMPLERS read the
      // same numbers differently: a ZERO CCW sweep (`start ≡ end mod 2π` — the
      // degenerate DIMANGULAR whose two rays coincide) keeps `sampleArc`'s
      // sweep at 0, which draws a run of COINCIDENT points (nothing), while the
      // DXF ELLIPSE rule `tessellateEllipse` implements reads equal parameters
      // as a FULL revolution. Every radius draws the same nothing for such an
      // arc, so the scalar reduction is EXACT for it — keeping it circular
      // preserves the pre-existing (invisible) output instead of materialising
      // a whole ellipse that no INSERT scale can justify.
      if (
        Math.abs(t.scale.x) !== Math.abs(t.scale.y) &&
        normalizeRad(prim.endAngle - prim.startAngle) !== 0
      ) {
        return transformDimensionEllipseArc(
          {
            center: prim.center,
            // A CIRCLE is the ellipse whose major semi-axis is `(r, 0)` with
            // ratio 1 — and then its parameter IS its polar angle, so the
            // emitter's `startAngle`/`endAngle` are already the parameters
            // the promotion re-anchors.
            majorAxis: { x: prim.radius, y: 0 },
            ratio: 1,
            startParam: prim.startAngle,
            endParam: prim.endAngle,
            colorHex: prim.colorHex,
          },
          basePoint,
          t,
        );
      }
      const mirrored = isMirroredTransform(t);
      const s = mapDirectionRad(prim.startAngle, t);
      const e = mapDirectionRad(prim.endAngle, t);
      return {
        ...prim,
        center: xfPoint(prim.center),
        radius: prim.radius * radiusScale,
        // `sampleArc` always sweeps CCW start→end; a mirror flips handedness,
        // so swap the mapped endpoints to keep the SAME visual arc.
        startAngle: mirrored ? e : s,
        endAngle: mirrored ? s : e,
      };
    }
    case "elliptic-arc":
      // Already an ellipse arc — ride the SAME conjugate-diameter treatment
      // through the SAME helper, with no `arc` special case in sight: a
      // circle is just the `ratio === 1` member of this family. (The typed
      // flatten COMPOSES nested INSERT frames into one `t` before it reaches
      // a leaf, so today only a caller that hands this function an
      // already-promoted primitive takes this arm; it is the general case the
      // `arc` arm above degrades to, not a second mechanism.)
      return transformDimensionEllipseArc(
        {
          center: prim.center,
          majorAxis: prim.majorAxis,
          ratio: prim.ratio,
          startParam: prim.startParam,
          endParam: prim.endParam,
          colorHex: prim.colorHex,
        },
        basePoint,
        t,
      );
    case "arrow":
    case "tick":
      // ONE arm for both marker kinds: they are the same unit prototype under
      // the same `T(tip)·Rz(direction)·S(size, size)` pose (`arrowMarkerMesh
      // .ts`), so they take the same full 2×2 glyph map — only the axis the
      // companion scalar is measured along differs, and `placeGlyphAffine`
      // asks the glyph itself for that.
      return {
        ...prim,
        tip: xfPoint(prim.tip),
        direction: xfDir(prim.direction),
        ...placeGlyphAffine(prim.direction, prim.size, prim.variant, t),
      };
    case "text":
      return {
        ...prim,
        position: xfPoint(prim.position),
        rotation: prim.rotation + scalarRotationDeg,
        height: prim.height * heightScale,
      };
  }
}
